import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import badmintonService, { BadmintonGameDetail } from '@/services/badmintonService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import { Card, CardContent, CardHeader } from '@/components/shadcn/card';
import Notification from '@/components/ui/Notification';
import BadmintonSetScorePanel from '@/components/community/badminton/BadmintonSetScorePanel';
import {
  formatBadmintonGameStatus,
  playersLabel,
} from '@/components/community/badminton/badmintonUiUtils';
import {
  buildSetsFromGame,
  isSet3Allowed,
  type SetScoreInput,
} from '@/components/community/badminton/badmintonScoringUtils';
import { isCustomerAppPath } from '@/utils/clientPaths';

export default function BadmintonRekapPage() {
  const { gameSlug, slug: eventSlugParam } = useParams<{ gameSlug: string; slug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientView = isCustomerAppPath(location.pathname);
  const [game, setGame] = useState<BadmintonGameDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sets, setSets] = useState<SetScoreInput[]>([]);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    message: string;
    variant: 'success' | 'error';
  }>({ isOpen: false, message: '', variant: 'success' });

  const loadGame = () => {
    if (!gameSlug) return;
    setLoading(true);
    badmintonService
      .getGame(gameSlug)
      .then((g) => {
        setGame(g);
        setSets(buildSetsFromGame(g.maxSets, g.setScores ?? []));
      })
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadGame();
  }, [gameSlug]);

  useEffect(() => {
    if (!game) return;
    const dest = isClientView
      ? `/event/${game.eventSlug}/badminton/game/${game.slug}/skor`
      : `/host/mabar/${game.eventSlug}/badminton/game/${game.slug}/skor`;
    navigate(dest, { replace: true });
  }, [game, isClientView, navigate]);

  const evSlug = game?.eventSlug ?? eventSlugParam;
  const sessionSlug = game?.sessionSlug;
  const backUrl =
    sessionSlug && evSlug
      ? isClientView
        ? `/event/${evSlug}/badminton/session/${sessionSlug}`
        : `/host/mabar/${evSlug}/badminton/session/${sessionSlug}`
      : evSlug
        ? isClientView
          ? `/event/${evSlug}/badminton`
          : `/host/mabar/${evSlug}/badminton`
        : isClientView
          ? '/event'
          : '/host/mabar';

  const handleSave = async () => {
    if (!gameSlug || !game?.canEdit) return;

    if (game.maxSets >= 3 && !isSet3Allowed(game.maxSets, sets)) {
      const set3 = sets.find((s) => s.setNumber === 3);
      if (set3 && (set3.scoreSideA > 0 || set3.scoreSideB > 0)) {
        setNotification({
          isOpen: true,
          message: 'Set 3 belum dapat diisi sebelum masing-masing sisi menang 1 set',
          variant: 'error',
        });
        return;
      }
    }

    for (const s of sets) {
      if (s.scoreSideA > 30 || s.scoreSideB > 30) {
        setNotification({
          isOpen: true,
          message: 'Skor maksimal 30 poin per set',
          variant: 'error',
        });
        return;
      }
    }

    setSaving(true);
    try {
      await badmintonService.updateScores(gameSlug, sets);
      setNotification({ isOpen: true, message: 'Skor berhasil disimpan', variant: 'success' });
      navigate(backUrl);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setNotification({
        isOpen: true,
        message: msg || 'Gagal menyimpan skor',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 py-6 max-w-3xl">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="mx-auto px-4 py-6 text-center">
        <p className="text-gray-600">Game tidak ditemukan</p>
        <ShadcnButton className="mt-4" onClick={() => navigate(backUrl)}>
          Kembali
        </ShadcnButton>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 max-w-3xl">
      <button
        type="button"
        onClick={() => navigate(backUrl)}
        className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-4"
      >
        <Icon icon="mdi:arrow-left" className="w-4 h-4" />
        Kembali ke Statistik
      </button>

      <Card className="rounded-xl mb-6">
        <CardHeader className="pb-2">
          <p className="text-sm text-gray-500">{game.sessionTitle}</p>
          <h1 className="text-xl font-bold text-gray-900">Rekap Data</h1>
          <p className="text-sm text-gray-600">
            {playersLabel(game.sideAPlayers)} vs {playersLabel(game.sideBPlayers)}
          </p>
          <p className="text-sm font-medium text-[#FF6B00]">
            Status: {formatBadmintonGameStatus(game.status)}
          </p>
        </CardHeader>
        <CardContent>
          <BadmintonSetScorePanel
            game={game}
            sets={sets}
            canEdit={game.canEdit}
            onSetsChange={setSets}
          />
          {game.canEdit && (
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <ShadcnButton
                className="bg-primary-500 flex-1"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Menyimpan...' : 'Simpan Skor'}
              </ShadcnButton>
            </div>
          )}
          {!game.canEdit && (
            <p className="text-sm text-gray-500 mt-4 text-center">Mode baca saja</p>
          )}
        </CardContent>
      </Card>

      {notification.isOpen && (
        <Notification
          isOpen={notification.isOpen}
          onClose={() => setNotification({ isOpen: false, message: '', variant: 'success' })}
          message={notification.message}
          variant={notification.variant}
        />
      )}
    </div>
  );
}
